<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import AgendaScopesAPI from 'dashboard/api/agendaScopes';
import AgendaScopeSelector from 'dashboard/routes/dashboard/kanban/agendas/AgendaScopeSelector.vue';
import {
  AGENT_TYPES,
  emptyScope,
} from 'dashboard/routes/dashboard/kanban/agendas/agendaScopes';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import SomenteLeituraOperadoMotor from '../components/SomenteLeituraOperadoMotor.vue';
import Chave from '../components/Chave.vue';
import {
  GRUPOS_NATIVOS,
  GRUPOS_MCP,
  travada,
  motivoDaTrava,
  nativasLigadas,
  alternarNativa,
  ferramentasDoGrupo,
  grupoMcpLigado,
  alternarGrupoMcp,
  integracoesDe,
  alternarIntegracoes,
} from '../habilidades';

const { t } = useI18n();
const carregando = ref(true);
const salvando = ref(false);
const erro = ref('');
const plano = ref(null);
const personaId = ref(null);
const grants = ref([]);
const mcpInterno = ref({ conexao_id: null, ferramentas: [] });
const handoffModo = ref('route');
// O que religar devolve: as integracoes que a persona tinha ao abrir a tela ou, se ja estavam desligadas, as
// integracoes HTTP da empresa.
const integracoesGuardadas = ref([]);
// Agendas que a persona pode usar (spec 011). Sem catalogo (modulo de Atendimentos desligado) o bloco nao aparece.
const personas = ref([]);
const agendaScope = ref(emptyScope());
const agendaCatalog = ref(null);

const carregarAgenda = async () => {
  try {
    const [lista, catalogo] = await Promise.all([
      AgendaScopesAPI.list(AGENT_TYPES.PERSONA),
      agendaCatalog.value ? null : AgendaScopesAPI.catalog(),
    ]);
    if (catalogo) agendaCatalog.value = catalogo.data.data;
    const salvo = lista.data.data.find(
      escopo => String(escopo.agent_id) === String(personaId.value)
    );
    agendaScope.value = salvo
      ? {
          practitioner_ids: salvo.practitioner_ids,
          user_ids: salvo.user_ids,
          conversation_assignee: salvo.conversation_assignee,
        }
      : emptyScope();
  } catch {
    agendaCatalog.value = null;
  }
};

const carregarPersona = async () => {
  const { data } = await AgenteIaAPI.habilidades(personaId.value);
  grants.value = data.grants || [];
  mcpInterno.value = data.mcp_interno;
  const atuais = integracoesDe(grants.value, data.mcp_interno?.conexao_id);
  integracoesGuardadas.value = atuais.length
    ? atuais
    : (data.catalogo?.http || []).map(definicao => ({
        source: 'HTTP',
        toolDefinitionId: definicao.id,
      }));
  handoffModo.value = data.settings?.handoff?.mode || 'route';
  await carregarAgenda();
};

const carregar = async () => {
  try {
    const { data: status } = await AgenteIaAPI.status();
    plano.value = status.plano;
    personaId.value = status.persona?.id;
    personas.value = (await AgenteIaAPI.personas()).data.payload || [];
    await carregarPersona();
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.HABILIDADES.ERRO');
  } finally {
    carregando.value = false;
  }
};

const trocarPersona = async id => {
  carregando.value = true;
  personaId.value = id;
  try {
    await carregarPersona();
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.HABILIDADES.ERRO');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const ligadas = computed(() => nativasLigadas(grants.value));
const trancadaNativa = nome => {
  const motivo = motivoDaTrava(nome, plano.value);
  return motivo ? t(motivo) : '';
};

const gruposMcp = computed(() =>
  GRUPOS_MCP.map(grupo => ({
    ...grupo,
    nomes: ferramentasDoGrupo(mcpInterno.value.ferramentas, grupo.prefixo),
  })).filter(grupo => grupo.nomes.length)
);

// Sem Agenda na conta (modulo de Atendimentos desligado) a opcao nao pode ser marcada; desligar uma que ficou ligada pode.
const trancadaGrupo = grupo => {
  if (grupo.chave !== 'AGENDA' || plano.value?.com_agenda !== false) return '';
  const ligado = grupoMcpLigado(
    grants.value,
    mcpInterno.value.conexao_id,
    grupo.nomes
  );
  return ligado ? '' : t('AGENTE_IA.HABILIDADES.MCP.AGENDA.SEM_AGENDA');
};

const ligarNativa = (nome, ligar) => {
  grants.value = alternarNativa(grants.value, nome, ligar);
};
const ligarGrupo = (grupo, ligar) => {
  grants.value = alternarGrupoMcp(
    grants.value,
    mcpInterno.value.conexao_id,
    grupo.nomes,
    ligar
  );
};

const integracoesLigadas = computed(
  () => integracoesDe(grants.value, mcpInterno.value.conexao_id).length > 0
);
// Ligada sempre pode desligar. Para ligar: so no avancado e com alguma integracao ja criada (elas nascem pelo
// Claude Code).
const trancadaIntegracoes = computed(() => {
  if (integracoesLigadas.value) return '';
  if (travada('send_image', plano.value)) {
    return t('AGENTE_IA.DISPONIVEL_NO_AVANCADO');
  }
  return integracoesGuardadas.value.length
    ? ''
    : t('AGENTE_IA.HABILIDADES.INTEGRACOES.PELO_CLAUDE');
});
const ligarIntegracoes = ligar => {
  grants.value = alternarIntegracoes(
    grants.value,
    mcpInterno.value.conexao_id,
    integracoesGuardadas.value,
    ligar
  );
};

// O bloco Agenda so existe com o grupo "Agenda" do nosso MCP ligado e o modulo de Atendimentos aberto.
const grupoAgenda = computed(() =>
  gruposMcp.value.find(grupo => grupo.chave === 'AGENDA')
);
const mostrarAgenda = computed(
  () =>
    !!agendaCatalog.value &&
    !!grupoAgenda.value &&
    grupoMcpLigado(
      grants.value,
      mcpInterno.value.conexao_id,
      grupoAgenda.value.nomes
    )
);

const salvar = async () => {
  salvando.value = true;
  try {
    await AgenteIaAPI.salvarHabilidades(personaId.value, {
      grants: grants.value,
      settings: { handoff: { mode: handoffModo.value } },
    });
    if (mostrarAgenda.value) {
      await AgendaScopesAPI.save({
        agentType: AGENT_TYPES.PERSONA,
        agentId: Number(personaId.value),
        practitionerIds: agendaScope.value.practitioner_ids,
        userIds: agendaScope.value.user_ids,
        conversationAssignee: agendaScope.value.conversation_assignee,
      });
      // A persona com escopo passa a usar a conexao propria no motor: recarrega para a tela seguir o id novo.
      await carregarPersona();
    }
    useAlert(t('AGENTE_IA.SALVO'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.HABILIDADES.ERRO'));
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.HABILIDADES.TITULO')"
    :description="t('AGENTE_IA.HABILIDADES.DESCRICAO')"
  >
    <SomenteLeituraOperadoMotor>
      <p v-if="carregando" class="text-sm text-n-slate-11">
        {{ t('AGENTE_IA.CARREGANDO') }}
      </p>
      <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

      <template v-else>
        <label
          v-if="personas.length > 1"
          class="flex flex-col gap-1"
          data-test="persona-select"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.HABILIDADES.PERSONA') }}
          </span>
          <select
            :value="personaId"
            class="w-full px-3 py-2 text-sm border rounded-lg sm:w-80 bg-n-alpha-black2 border-n-weak text-n-slate-12"
            @change="trocarPersona($event.target.value)"
          >
            <option
              v-for="persona in personas"
              :key="persona.id"
              :value="persona.id"
            >
              {{ persona.name }}
            </option>
          </select>
        </label>

        <section
          v-for="grupo in GRUPOS_NATIVOS"
          :key="grupo.chave"
          class="flex flex-col gap-2"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t(`AGENTE_IA.HABILIDADES.GRUPOS.${grupo.chave}`) }}
          </h2>
          <Chave
            v-for="nome in grupo.ferramentas"
            :key="nome"
            :model-value="ligadas.includes(nome)"
            :rotulo="t(`AGENTE_IA.HABILIDADES.NATIVAS.${nome}`)"
            :trancado="trancadaNativa(nome)"
            @update:model-value="ligarNativa(nome, $event)"
          />
          <label
            v-if="grupo.chave === 'CONVERSA'"
            class="flex flex-col gap-1 pl-3"
          >
            <span class="text-xs text-n-slate-11">
              {{ t('AGENTE_IA.HABILIDADES.PASSAR.TITULO') }}
            </span>
            <select
              v-model="handoffModo"
              class="w-full px-3 py-2 text-sm border rounded-lg sm:w-80 bg-n-alpha-black2 border-n-weak text-n-slate-12"
            >
              <option
                v-for="modo in ['route', 'agent_choice']"
                :key="modo"
                :value="modo"
              >
                {{ t(`AGENTE_IA.HABILIDADES.PASSAR.${modo.toUpperCase()}`) }}
              </option>
            </select>
          </label>
        </section>

        <section v-if="gruposMcp.length" class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.HABILIDADES.GRUPOS.ATENDE') }}
          </h2>
          <Chave
            v-for="grupo in gruposMcp"
            :key="grupo.chave"
            :model-value="
              grupoMcpLigado(grants, mcpInterno.conexao_id, grupo.nomes)
            "
            :rotulo="t(`AGENTE_IA.HABILIDADES.MCP.${grupo.chave}.TITULO`)"
            :ajuda="t(`AGENTE_IA.HABILIDADES.MCP.${grupo.chave}.AJUDA`)"
            :trancado="trancadaGrupo(grupo)"
            @update:model-value="ligarGrupo(grupo, $event)"
          />
        </section>

        <section
          v-if="mostrarAgenda"
          class="flex flex-col gap-2"
          data-test="agenda-block"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENDA_SCOPES.TITLE') }}
          </h2>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('AGENDA_SCOPES.DESCRIPTION') }}
          </p>
          <AgendaScopeSelector v-model="agendaScope" :catalog="agendaCatalog" />
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.HABILIDADES.GRUPOS.INTEGRACOES') }}
          </h2>
          <Chave
            :model-value="integracoesLigadas"
            :rotulo="t('AGENTE_IA.HABILIDADES.INTEGRACOES.TITULO')"
            :ajuda="t('AGENTE_IA.HABILIDADES.INTEGRACOES.AJUDA')"
            :trancado="trancadaIntegracoes"
            @update:model-value="ligarIntegracoes"
          />
        </section>

        <div>
          <Button
            :label="t('AGENTE_IA.SALVAR')"
            :is-loading="salvando"
            @click="salvar"
          />
        </div>
      </template>
    </SomenteLeituraOperadoMotor>
  </AgenteIaLayout>
</template>
